/**
 * runtimeConfig.ts — runtime Cognito configuration for the SPA
 *
 * The deployed SPA fetches GET /api/config.json at boot to learn its Cognito User
 * Pool ID + Client ID, instead of having them baked into the JS bundle at build time.
 * This lets one immutable container image serve the front-end in any environment
 * (the API injects the right Cognito IDs from its own env — see apps/api routes/config.ts).
 *
 * getCognitoConfig() resolution order:
 *   1. runtime values loaded from /api/config.json (production)
 *   2. build-time VITE_COGNITO_* env (local dev / tests, where there is no API to ask)
 */

export interface CognitoConfig {
  userPoolId: string
  clientId: string
}

let cached: CognitoConfig | null = null

/**
 * Fetch /api/config.json and cache the Cognito config on success. Safe to call once
 * at app startup. On any failure (network error, non-ok, missing fields) it leaves the
 * cache untouched so getCognitoConfig() falls back to build-time env — never throws.
 */
export async function loadRuntimeConfig(fetchFn: typeof fetch = fetch): Promise<void> {
  try {
    const res = await fetchFn('/api/config.json', {
      headers: { accept: 'application/json' },
    })
    if (!res.ok) return
    const data = (await res.json()) as Partial<CognitoConfig>
    if (data.userPoolId && data.clientId) {
      cached = { userPoolId: data.userPoolId, clientId: data.clientId }
    }
  } catch {
    // Leave cache as-is; getCognitoConfig() falls back to build-time env.
  }
}

/** Resolve Cognito config: runtime values if loaded, else build-time VITE_ env. */
export function getCognitoConfig(): CognitoConfig {
  if (cached) return cached
  return {
    userPoolId: (import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined) ?? '',
    clientId: (import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined) ?? '',
  }
}

/** Test-only: clear the module-level cache between tests. */
export function __resetRuntimeConfigForTest(): void {
  cached = null
}
